import React from "react";
import { formatDisplayDate } from "utils/dateFormat";
import logo from "../../../assets/img/logo.png";
import { getInvoicePaidAmount } from "utils/invoiceDashboardUtils";
import { formatInvoiceAmount } from "utils/invoiceAmountFormat";

const InvoiceTemplate = ({ printData, contentRef }) => {
  const getIncludedGSTPrice = () => {
    const total = printData?.invoices
      ?.filter((item) => item?.gst === "yes" || item?.gst === "true" || item?.gst === true)
      ?.reduce((sum, item) => sum + Number(item?.total) * 0.1, 0);
    return Number(total).toFixed(2);
  };

  const getSubTotal = () => {
    return printData?.invoices?.reduce(
      (sum, item) => sum + Number(item?.quantity) * Number(item?.price),
      0
    );
  };

  const getInvoiceNumber = () => {
    if (printData?.companyName) {
      if (printData?.companyName?.includes("KOTHAR GROUP")) {
        return "KES-INV-" + printData?.invoiceId;
      }
      if (printData?.companyName?.includes("KOTHAR TECH")) {
        return "KS-INV-" + printData?.invoiceId;
      }
      if (printData?.companyName?.includes("CAM YOUTH")) {
        return "CYA-INV-" + printData?.invoiceId;
      } else {
        return "KES-INV-" + printData?.invoiceId;
      }
    } else {
      return "KES-INV-" + printData?.invoiceId;
    }
  };

  const getTotal = () => Number(printData?.subTotal || 0) + Number(getIncludedGSTPrice());

  // Verified payments are money already received, so the printed copy has to net them off rather
  // than restate the whole invoice as outstanding. Both rows stay hidden while nothing is paid,
  // which is every freshly raised invoice.
  const paidAmount = getInvoicePaidAmount(printData, getTotal());
  const balanceDue = Math.max(getTotal() - paidAmount, 0);

  return (
    <div className="printContent" ref={contentRef}>
      <div className="invoice-box">
        <div className="flex flex-col justify-between h-full">
          <div>
            <div className="invoice-header">
              <div className="invoice-heading">
                <div className="invoice-title">
                  TAX INVOICE{" "}
                  {printData?.status && (
                    <div className="paid-tag">
                      <span className="paid-text">PAID</span>
                    </div>
                  )}
                </div>
                <div className="right-side">
                  <img src={printData?.logo || logo} alt="Company Logo" className="logo" />
                </div>
              </div>
              <div className="top-section">
                <div className="client-info">
                  <p className="font-semibold">{printData?.invoiceTo}</p>
                  <p>{printData?.address}</p>
                </div>
                <div className="invoice-detail">
                  <p className="font-semibold">Invoice Date</p>
                  <p>{formatDisplayDate(printData?.issueDate || new Date())}</p>
                  {/* <p>10-May-2025</p> */}
                  <br />

                  <p className="font-semibold">Invoice Number</p>
                  <p>{getInvoiceNumber()}</p>
                </div>
                <div className="kothar-info">
                  <p>{printData?.companyName || "Kothar Group PTY LTD"}</p>
                  <p>{printData.location || "Unit 273/398 Pitt St, Haymarket NSW 2000"}</p>
                  <p>ABN: {printData.invoiceFromAbn || "21 650 886 301"}</p>

                  <p>{printData.contact || "+61 480 322 403"}</p>

                  <p>{printData.email || "info@kotharedu.com"}</p>
                </div>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Quantity</th>
                  <th>Unit Price</th>
                  <th>GST</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {printData?.invoices?.map((item, key) => (
                  <tr key={key}>
                    <td>{item?.description}</td>
                    <td>{item?.quantity}</td>
                    <td>{formatInvoiceAmount(item?.price ?? 0)}</td>
                    <td>
                      {item?.gst === "yes" || item?.gst === "true" || item?.gst === true
                        ? "10%"
                        : "-"}
                    </td>
                    <td>
                      {formatInvoiceAmount(
                        item?.total ?? Number(item?.price) * Number(item?.quantity)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="sub-total">
              <p>
                Subtotal: &nbsp;&nbsp;&nbsp;&nbsp;
                <span>
                  {formatInvoiceAmount(printData?.subTotal ? printData?.subTotal : getSubTotal())}
                </span>
              </p>
              <p>
                Total GST 10%: &nbsp;&nbsp;&nbsp;&nbsp;
                <span>{formatInvoiceAmount(getIncludedGSTPrice())}</span>
              </p>
            </div>
            <div className=""></div>
            <p className="total">
              Total AUD: &nbsp;&nbsp;&nbsp;&nbsp;
              {formatInvoiceAmount(getTotal())}
            </p>
            {paidAmount > 0 && (
              <div className="sub-total">
                <p>
                  Amount Paid: &nbsp;&nbsp;&nbsp;&nbsp;
                  <span>{paidAmount.toFixed(2)}</span>
                </p>
                <p>
                  Balance Due: &nbsp;&nbsp;&nbsp;&nbsp;
                  <span>{balanceDue.toFixed(2)}</span>
                </p>
              </div>
            )}
          </div>
          <div className="payment-advice mb-24">
            <h2 className="font-bold">PAYMENT ADVICE</h2>
            <div className="payment-advice-body">
              <div className="payment-advice-to">
                <div className="kothar-info">
                  <p>Account Name: {printData?.paymentInfo?.accountName}</p>
                  <p>Account Number: {printData?.paymentInfo?.accountNumber}</p>
                  <p>BSB Number: {printData?.paymentInfo?.bankDetails}</p>
                  <p>
                    Reference: {printData?.invoiceTo} - {getInvoiceNumber()}
                  </p>
                </div>
              </div>
              <div className="payment-advice-customer">
                <table>
                  <tbody>
                    <tr>
                      <td>Customer</td>
                      <td>{printData?.invoiceTo}</td>
                    </tr>
                    <tr>
                      <td>Invoice Number</td>
                      <td>{getInvoiceNumber()}</td>
                    </tr>
                    {paidAmount > 0 && (
                      <>
                        <tr>
                          <td>Invoice Total</td>
                          <td>{formatInvoiceAmount(getTotal())}</td>
                        </tr>
                        <tr>
                          <td>Amount Paid</td>
                          <td>{paidAmount.toFixed(2)}</td>
                        </tr>
                      </>
                    )}
                    <tr>
                      <td>Amount Due</td>
                      <td className="font-bold">{balanceDue.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td>Due Date</td>
                      <td>{printData?.dueDate}</td>
                    </tr>
                    {printData?.isPaid && (
                      <tr>
                        <td>Status</td>
                        <td className="font-bold text-green-600">PAID</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceTemplate;
